import { KitsTab } from '@components/Product/Shared/KitsTab/KitsTab';
import { faCircleInfo, faListTree, faObjectGroup } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import type { FeatureFlag } from '@partful/shared-types';
import { KitNamedLink } from '@services/kits/kit';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  EmptyPartsTab,
  EmptyPartsText,
  StyledTabContainer,
  StyledTabContentPanel,
} from 'src/components/Product/Mobile/Panels/PartsCatalogue/styles';
import { PartsTab } from 'src/components/Product/Shared/PartsTab/PartsTab';
import { NamedLink, usePartNavigation } from 'src/services/partNavigation';
import { z } from 'zod';
import { Tab, Tabs } from './InnerTabs/Tab';

const IconTab = styled(Tab)`
  min-height: 48px;
  color: ${({ theme }) => theme.palette.sidebarBackground.contrastText};
`;

interface SingleTabPartsPanelProps {
  partChildren: NamedLink[];
  kitsToDisplay?: KitNamedLink[];
  enabledFeatures: FeatureFlag[];
}

export default function SingleTabPartsPanel({
  partChildren,
  kitsToDisplay,
  enabledFeatures,
}: Readonly<SingleTabPartsPanelProps>) {
  const { state } = useLocation();
  const { t } = useTranslation('partsCatalogue');
  const [currentTab, setCurrentTab] = useState('parts');

  useMemo(() => {
    const stateSchema = z
      .object({
        activeSidebarTab: z.literal('kits').or(z.literal('parts')),
      })
      .default({ activeSidebarTab: 'parts' });

    const result = stateSchema.safeParse(state);

    return result.success ? setCurrentTab(result.data.activeSidebarTab) : setCurrentTab('parts');
  }, [state, setCurrentTab]);

  const { parents } = usePartNavigation();

  const showKits =
    enabledFeatures.includes('configuration-management') &&
    enabledFeatures.includes('kits') &&
    parents.length === 0 &&
    (kitsToDisplay?.length ?? 0) > 0;

  if (!partChildren.length) {
    return (
      <EmptyPartsTab>
        <FontAwesomeIcon icon={faCircleInfo} size="2x" />
        <EmptyPartsText>{t('noPartsToBuy')}</EmptyPartsText>
      </EmptyPartsTab>
    );
  }

  return (
    <StyledTabContainer>
      <Tabs value={currentTab} data-testid="parts-tab-panel">
        <IconTab
          label={t('parts')}
          value="parts"
          icon={<FontAwesomeIcon icon={faListTree} />}
          iconPosition="start"
          inner
          onClick={() => setCurrentTab('parts')}
        />
        {showKits && (
          <IconTab
            label={t('kitSection.kit_other')}
            value="kits"
            icon={<FontAwesomeIcon icon={faObjectGroup} />}
            iconPosition="start"
            inner
            onClick={() => setCurrentTab('kits')}
          />
        )}
      </Tabs>
      <StyledTabContentPanel>
        {currentTab === 'parts' && partChildren && <PartsTab partChildren={partChildren} />}
        {currentTab === 'kits' && showKits && <KitsTab kits={kitsToDisplay!} />}
      </StyledTabContentPanel>
    </StyledTabContainer>
  );
}
